/**
 * GoalStatusChip — direction-aware on-track indicator.
 *
 * Computes status from the latest value vs the goal target, with extra
 * nuance for "behind but improving" / "on-target but flat with deadline
 * approaching." Used by:
 *   - the Goals page card header (D3)
 *   - per-widget overlay when a widget's measure is goal-tracked (Phase D2)
 *
 * Phase D3/D4 of issue #90 v3 plan.
 */
import type { Goal } from '../../types/exec'

interface Props {
  goal: Goal
  /** Latest known value (most recent FY) for the goal's metric in scope. */
  latestValue: number | null | undefined
  /** Optional prior-FY value to compute slope (for "improving" detection). */
  priorValue?: number | null | undefined
  /** Compact size for widget headers. */
  size?: 'sm' | 'md'
}

type Status = 'on_track' | 'behind' | 'above_target' | 'behind_improving' | 'on_target_flat' | 'no_data'

function computeStatus(goal: Goal, latest: number | null | undefined, prior: number | null | undefined): Status {
  if (latest === null || latest === undefined) return 'no_data'

  const target = goal.target_value.value
  const isLower = goal.target_value.direction === 'lower_is_better'

  // Trend (improvement = moving toward target relative to direction)
  const slope = prior === null || prior === undefined ? null : latest - prior
  const improving = slope === null
    ? null
    : isLower ? slope < 0 : slope > 0

  // Are we hitting the target?
  const meetsTarget = isLower ? latest <= target : latest >= target

  if (meetsTarget) {
    // Above target = exceeding (good); flat near deadline is its own callout
    const aboveBy = isLower ? (target - latest) / Math.max(target, 1) : (latest - target) / Math.max(target, 1)
    if (aboveBy > 0.10) return 'above_target'
    // Within 10% of target with little slope and deadline approaching → "on target but flat"
    if (improving === false && goal.effective_to) {
      const today = new Date()
      const deadline = new Date(goal.effective_to)
      const monthsLeft = (deadline.getFullYear() - today.getFullYear()) * 12 + (deadline.getMonth() - today.getMonth())
      if (monthsLeft <= 6 && monthsLeft >= 0) return 'on_target_flat'
    }
    return 'on_track'
  }

  // Below target. If improving, soften the message.
  if (improving === true) return 'behind_improving'
  return 'behind'
}

const STATUS_LABELS: Record<Status, string> = {
  on_track: 'On Track',
  behind: 'Behind',
  above_target: 'Above Target',
  behind_improving: 'Behind, Improving',
  on_target_flat: 'On Target, Flat',
  no_data: 'No Data',
}

const STATUS_CLASSES: Record<Status, string> = {
  on_track: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  behind: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  above_target: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  behind_improving: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  on_target_flat: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  no_data: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
}

export default function GoalStatusChip({ goal, latestValue, priorValue, size = 'sm' }: Props) {
  const status = computeStatus(goal, latestValue, priorValue)
  const label = STATUS_LABELS[status]
  const cls = STATUS_CLASSES[status]
  const sizeCls = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'

  return (
    <span className={`inline-flex items-center rounded-full font-medium ${cls} ${sizeCls}`}>
      {label}
    </span>
  )
}
